import React, { createContext, Dispatch, FC, ReactNode, useContext, useEffect, useState } from 'react';
import { Student } from '../types';
import { errorToast } from '../../../CommonComponent/toast';
import useFetchWithAuth from '../../../CommonHooks/useFetchWithAuth';
import { useMainContainerProvider } from '../../../mainContainerProvider';

interface ManageStudentContextType {
  isDetailReadOnly: boolean;
  setIsDetailsReadOnly: Dispatch<React.SetStateAction<boolean>>;
  filteredList: Student[];
  setFilteredList: Dispatch<React.SetStateAction<Student[]>>;
  setInitialData: Dispatch<React.SetStateAction<Student[] | undefined>>;
  initialData: Student[] | undefined;
  setIsNew: Dispatch<React.SetStateAction<boolean>>;
  isNew: boolean;
  isLoading: boolean;
  getStudentList: () => Promise<void>;
  setIsLoading: Dispatch<React.SetStateAction<boolean>>;
  setSelectedStudent: Dispatch<React.SetStateAction<Student | undefined>>;
  selectedStudent: Student | undefined;
  setErrorState: Dispatch<React.SetStateAction<{ field: string; error: string }[]>>;
  errorState?: { field: string; error: string }[];
}

const ManageStudentContext = createContext<ManageStudentContextType | undefined>(undefined);

const ManageStudentProvider: FC<{ children: ReactNode }> = ({ children }) => {
  const [isDetailReadOnly, setIsDetailsReadOnly] = useState<boolean>(true);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [initialData, setInitialData] = useState<Student[]>();
  const [filteredList, setFilteredList] = useState<Student[]>([]);
  const [selectedStudent, setSelectedStudent] = useState<Student>();
  const { fetchWithAuth } = useFetchWithAuth();
  const [isNew, setIsNew] = useState<boolean>(true);
  const { userType, instituteId } = useMainContainerProvider();
  const [errorState, setErrorState] = useState<{ field: string; error: string }[]>([]);

  const getStudentList = async () => {
    setIsLoading(true);
    const url = `student-list/?userType=${userType}&instituteId=${instituteId}&studentId=&fullName=`;
    const { error, data } = await fetchWithAuth(url);
    if (error) {
      errorToast(error);
      setIsLoading(false);
    } else {
      setInitialData(data);
      setIsLoading(false);
    }
  };

  useEffect(() => {
    getStudentList();
    // eslint-disable-next-line
  }, []);

  return (
    <ManageStudentContext.Provider
      value={{
        isDetailReadOnly,
        setIsDetailsReadOnly,
        filteredList,
        setFilteredList,
        initialData,
        setInitialData,
        setIsNew,
        isNew,
        isLoading,
        getStudentList,
        setIsLoading,
        setSelectedStudent,
        selectedStudent,
        setErrorState,
        errorState,
      }}
    >
      {children}
    </ManageStudentContext.Provider>
  );
};

export const useManageStudent = (): ManageStudentContextType => {
  const context = useContext(ManageStudentContext);
  if (!context) {
    throw new Error('useInstituteProvider must be used within an InstituteProfileProvider');
  }
  return context;
};

export default ManageStudentProvider;
